import { useRef, type ReactNode } from 'react';
import {
  Dialog as ShadcnDialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';

export type DialogProps = { open: boolean; title: string; onClose: () => void; children: ReactNode };

/**
 * A modal panel on Radix Dialog.
 *
 * Previously the native `<dialog>` element. It gave focus trapping and Escape
 * for free, but it also anchored the panel to the top-left of the viewport
 * with no entrance, and jsdom implements neither `showModal()` nor `close()`
 * so every test needed a hand-written shim. Radix centres the panel, animates
 * it in on opacity and scale, traps focus, marks the rest of the page
 * `aria-hidden`, and needs no jsdom support at all.
 *
 * The prop shape is unchanged: `open` is controlled and `onClose` fires for
 * Escape, the overlay and the close button alike.
 */
export function Dialog({ open, title, onClose, children }: DialogProps) {
  /*
   * Radix restores focus to its own `DialogTrigger` on close. This dialog is
   * opened by whatever the screen felt like — a row button, a menu item — and
   * `open` is a controlled prop, so there is no trigger for Radix to find and
   * focus lands on <body> instead. Remembering the active element ourselves is
   * the fix, and it has to happen in the render that flips `open`: by the time
   * an effect runs, Radix has already moved focus into the panel.
   */
  const wasOpen = useRef(open);
  const restoreTo = useRef<HTMLElement | null>(null);
  if (open && !wasOpen.current) {
    restoreTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasOpen.current = open;

  return (
    <ShadcnDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        onCloseAutoFocus={(event) => {
          // Preventing the default suppresses Radix's own restore, which would
          // aim at a trigger that does not exist here.
          event.preventDefault();
          restoreTo.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>{children}</DialogBody>
      </DialogContent>
    </ShadcnDialog>
  );
}
